import type { ListingType } from './listings';

/**
 * Drafts made on this device that the service doesn't hold yet (021). On the
 * web they live only in the open page (FR-007a). Every id is minted here and
 * becomes the server's id, so a repeated request names the same thing (R1).
 */

/** The 016 per-listing photo limit, enforced on the device before sync (FR-009e). */
export const MAX_DRAFT_PHOTOS = 20;

/** The content a draft is created with; `collectionId` is fixed once the service holds it. */
export type DraftFields = {
  collectionId: string;
  title: string;
  description: string;
  listingType: ListingType;
  price: string | null;
  location: string;
  expiresAt: string | null;
};

/** A photo picked for a draft, already copied into the page (R7). */
export type NewDraftPhoto = { file: File; width: number; height: number };

/** `unreadable`: the file can't be read any more; `rejected`: the service refused it. */
export type PhotoFailure = 'unreadable' | 'rejected';

export type UnsyncedPhoto = {
  /** Becomes the pending image's id and then the listing photo's id. */
  id: string;
  file: File;
  width: number;
  height: number;
  /** The service holds it as a pending image (or already on the listing). */
  uploaded: boolean;
  failure: PhotoFailure | null;
};

/** What stops a draft until something changes (FR-009). */
export type DraftBlocker = 'needsSignIn' | 'needsVerification' | 'chooseCollection';

export type UnsyncedDraft = {
  /** Becomes the listing's id. */
  id: string;
  accountId: string;
  /** Epoch ms; the Drafts order, and sync goes oldest first. */
  createdAt: number;
  fields: DraftFields;
  /** In listing order. */
  photos: UnsyncedPhoto[];
  /** +1 on every local save. */
  localRevision: number;
  /** The revision whose fields the service is known to hold; null before the create is confirmed. */
  sentRevision: number | null;
  /** The create was answered, so the listing exists on the service. */
  serverConfirmed: boolean;
  /** Photo ids on the server listing, in its order, as last answered. */
  serverPhotoIds: string[];
  blocker: DraftBlocker | null;
  /** Epoch ms of the next try while backing off. */
  nextAttemptAt: number | null;
};

/** A removed draft whose server copy (if any) and uploads are still to be deleted (FR-009c). */
export type RemovalTombstone = { draftId: string; photoIds: string[] };

/**
 * A local save. `photoIds` is the new order of the photos to keep (left out =
 * removed); `addPhotos` go after them.
 */
export type DraftEdit = {
  fields?: Partial<DraftFields>;
  photoIds?: string[];
  addPhotos?: NewDraftPhoto[];
};

/** Raised by a save that would take a draft past `MAX_DRAFT_PHOTOS`. */
export class TooManyPhotosError extends Error {
  constructor() {
    super(`A listing can have up to ${MAX_DRAFT_PHOTOS} photos.`);
    this.name = 'TooManyPhotosError';
  }
}

/** Raised by a transport that can no longer read a photo's file. */
export class UnreadablePhotoError extends Error {
  constructor(readonly photoId: string) {
    super("Couldn't read this photo.");
    this.name = 'UnreadablePhotoError';
  }
}

/** What an unsynced draft shows, in FR-009's words only (FR-009a). */
export type DraftSyncState =
  | 'photoFailed'
  | 'chooseCollection'
  | 'needsSignIn'
  | 'needsVerification'
  | 'waitingForConnection'
  | 'syncing';

export const DRAFT_SYNC_LABELS: Record<DraftSyncState, string> = {
  photoFailed: "Couldn't add this photo",
  chooseCollection: 'Choose another collection',
  needsSignIn: 'Waiting for you to sign in',
  needsVerification: 'Verify your email or phone to sync',
  waitingForConnection: 'Waiting for connection',
  syncing: 'Syncing…',
};

/** The one state a draft shows; the first that applies wins. */
export function draftSyncState(draft: UnsyncedDraft, online: boolean): DraftSyncState {
  if (draft.photos.some((p) => p.failure !== null)) return 'photoFailed';
  if (draft.blocker) return draft.blocker;
  if (!online || draft.nextAttemptAt !== null) return 'waitingForConnection';
  return 'syncing';
}
